import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { Jose } from 'jose-jwe-jws';
import { environment } from '@environments/environment';
import urlJoin from 'url-join';
import { ProcessEventService } from '@shared/services/process-event.service';
import { ProcessEventFactory } from '@shared/models/processEvents';
import { assert } from 'ts-essentials';
import { ConnectionFormItemDto, ConnectionRequestDto, LoginResponseDto } from './dtos/loginDtos';
import { ConnectionResponseDto, ConnectionStateEnum } from './dtos/connection-response';
import { CompleteSigninRequest } from './dtos/complete-signin-request';
import { AccountsService } from '../../select-accounts/services/accounts.service';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private urlOpenBanking = environment.urlOpenBanking;

  constructor(
    private http: HttpClient,
    private accountsService: AccountsService,
    private processEventService: ProcessEventService
  ) {}

  connect(input: {
    userToken: string;
    connectionRequest: ConnectionRequestDto;
    userId: string;
  }): Observable<LoginResponseDto> {
    const { connectionRequest, userId, userToken } = input;

    const connectUrl = urlJoin(this.urlOpenBanking, 'auth', 'signin');

    return new Observable((observer) =>
      this.http
        .post<ConnectionResponseDto>(
          connectUrl,
          {
            userId,
            bankId: connectionRequest.external_bank_id,
            form: connectionRequest.form,
          },
          {
            headers: {
              Authorization: `Bearer ${userToken}`,
            },
          }
        )
        .subscribe({
          next: (response) => {
            switch (response.state) {
              case ConnectionStateEnum.DECOUPLED:
                observer.next({
                  sca_third_party: true,
                  accounts: [],
                  form: [],
                  connectionId: response.id,
                });
                break;
              case ConnectionStateEnum.MORE_INFORMATION:
                observer.next({
                  sca_third_party: false,
                  accounts: [],
                  form: response.form ?? null,
                  connectionId: response.id,
                });
                break;
              case ConnectionStateEnum.VALID:
                this.accountsService
                  .getAccounts({
                    userId: response.userId,
                    userToken: userToken,
                    connectionId: response.id,
                  })
                  .subscribe({
                    next: (getAccountsResponse) => {
                      observer.next({
                        sca_third_party: false,
                        accounts: getAccountsResponse,
                        form: response.form ?? null,
                        connectionId: response.id,
                      });
                    },
                    error: (err) => observer.error(err),
                  });
                break;
              default:
                throw new Error('Unknown state');
            }
          },
          error: (err) => observer.error(err),
        })
    );
  }

  sendOneTimePassword(input: {
    userToken: string;
    connectionId: string;
    form: ConnectionFormItemDto[];
    userId: string;
  }): Observable<LoginResponseDto> {
    const { connectionId, form, userId, userToken } = input;

    const completeSigninUrl = urlJoin(this.urlOpenBanking, 'auth', 'sca');

    const request: CompleteSigninRequest = {
      connectionId: connectionId,
      form: form,
      userId,
    };

    return new Observable((observer) =>
      this.http
        .post<ConnectionResponseDto>(completeSigninUrl, request, {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        })
        .subscribe({
          next: (response) => {
            switch (response.state) {
              case ConnectionStateEnum.SCA:
                observer.next({
                  sca_third_party: true,
                  accounts: [],
                  form: [],
                  connectionId: response.id,
                });
                break;
              case ConnectionStateEnum.MORE_INFORMATION:
                observer.next({
                  sca_third_party: false,
                  accounts: [],
                  form: response.form ?? null,
                  connectionId: response.id,
                });
                break;
              case ConnectionStateEnum.VALID:
                this.accountsService
                  .getAccounts({
                    userId: response.userId,
                    userToken: userToken,
                    connectionId: response.id,
                  })
                  .subscribe({
                    next: (getAccountsResponse) => {
                      observer.next({
                        sca_third_party: false,
                        accounts: getAccountsResponse,
                        form: response.form ?? [],
                        connectionId: response.id,
                      });
                    },
                    error: (err) => observer.error(err),
                  });
                break;
              default:
                throw new Error('Unknown state');
            }
          },
          error: (err) => observer.error(err),
        })
    );
  }

  /**
   * Used to encrypt users credential when connecting to the bank
   * @returns a public key to encrypt credentials with
   */
  getCredentialsEncryptionPublicKey(): Observable<{ publicKey: string }> {
    const publickKeyUrl = urlJoin(this.urlOpenBanking, 'encryption', 'public-key');

    return new Observable((observer) =>
      this.http.get<{ publicKey: string }>(publickKeyUrl).subscribe({
        next: (response) => {
          observer.next(response);
        },
        error: (error) => {
          this.processEventService.push(
            ProcessEventFactory.SpawnInternalErrorEvent({
              errorOrigin: 'LoginService - getCredentialsEncryptionPublicKey',
              error,
            })
          );
        },
      })
    );
  }

  async encryptConnectionForm(payload: ConnectionRequestDto): Promise<ConnectionRequestDto> {
    try {
      const encryptedForm: ConnectionFormItemDto[] = [];
      // const encoder = new TextEncoder();
      // const aggregationEncryption = await this.getAggregationEncryptionPublicKey().pipe(first()).toPromise();
      const encryption = await firstValueFrom(this.getCredentialsEncryptionPublicKey());
      const jwkRsa = JSON.parse(encryption.publicKey) as JWKRSA;
      const publicKey = await Jose.Utils.importRsaPublicKey(jwkRsa, 'RSA-OAEP');
      const cryptographer = new Jose.WebCryptographer();
      const encrypter = new Jose.JoseJWE.Encrypter(cryptographer, publicKey);
      assert(jwkRsa.kid != null, 'kid should be defined.');
      encrypter.addHeader('kid', jwkRsa.kid);

      for (const value of payload.form) {
        const encryptedValue =
          value.name === 'openapiwebsite' ? value.value : await encrypter.encrypt(value.value);
        encryptedForm.push({ value: encryptedValue, name: value.name });
      }

      payload.form = encryptedForm;
      return payload;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }
}
